// LxStatus — canonical status pill (single source for ALL modules)
//
// Source of truth: Make Wave 0 unified Design System §2 (status chips),
// bundle Lamtix_2026-06-12_04-24-35_Make/DesignSystemPage.tsx STATUS_MAP.
// Replaces the ~15 per-module status-colour maps (InvoiceStatusBadge,
// chequeUtils, ContractStatusBadge, …) that drifted apart. One map, six tones.
//
// Canonical resolution: issued/sent = INFO blue, partial/pending = WARNING amber,
// under-review/expired = PURPLE, paid/approved/active = SUCCESS green,
// overdue/cancelled/rejected = DANGER red, draft/ended = NEUTRAL.
//
// Colours are the exact Make hex values (inline) so the canonical is pixel-exact
// across modules; dark-mode token-fold is a later platform-wide pass.
//
// D-SHADCN-LAMTIX-WRAPPED-01: sole import path is @lamtix/ui

export type StatusTone = 'neutral' | 'info' | 'success' | 'danger' | 'warning' | 'purple';

const TONES: Record<StatusTone, { bg: string; text: string }> = {
  neutral: { bg: '#F0F5F1', text: '#5A7065' },
  info:    { bg: '#EBF4FF', text: '#0D7DF2' },
  success: { bg: '#E8F8EF', text: '#036635' },
  danger:  { bg: '#FDECEA', text: '#DC3232' },
  warning: { bg: '#FFF8E6', text: '#B57800' },
  purple:  { bg: '#F5F0FF', text: '#7C3AED' },
};

interface StatusDef { tone: StatusTone; ar: string; en: string }

// Canonical registry — keyed by normalized DB status key. Extend as modules add
// states; unknown keys fall back to NEUTRAL with the raw label (never crash).
const REGISTRY: Record<string, StatusDef> = {
  draft:            { tone: 'neutral', ar: 'مسودة', en: 'Draft' },
  issued:           { tone: 'info',    ar: 'صادرة', en: 'Issued' },
  sent:             { tone: 'info',    ar: 'مُرسلة', en: 'Sent' },
  paid:             { tone: 'success', ar: 'مدفوعة', en: 'Paid' },
  partially_paid:   { tone: 'warning', ar: 'جزئية', en: 'Partial' },
  partial:          { tone: 'warning', ar: 'جزئية', en: 'Partial' },
  overdue:          { tone: 'danger',  ar: 'متأخرة', en: 'Overdue' },
  cancelled:        { tone: 'danger',  ar: 'ملغاة', en: 'Cancelled' },
  canceled:         { tone: 'danger',  ar: 'ملغاة', en: 'Cancelled' },
  pending:          { tone: 'warning', ar: 'معلّقة', en: 'Pending' },
  approved:         { tone: 'success', ar: 'مُعتمدة', en: 'Approved' },
  rejected:         { tone: 'danger',  ar: 'مرفوضة', en: 'Rejected' },
  under_review:     { tone: 'purple',  ar: 'قيد المراجعة', en: 'Under Review' },
  manual_review:    { tone: 'purple',  ar: 'قيد المراجعة', en: 'Under Review' },
  collected:        { tone: 'success', ar: 'مقبوضة', en: 'Collected' },
  active:           { tone: 'success', ar: 'نشط', en: 'Active' },
  ended:            { tone: 'neutral', ar: 'منتهي', en: 'Ended' },
  pending_approval: { tone: 'warning', ar: 'بانتظار الموافقة', en: 'Pending Approval' },
  expired:          { tone: 'purple',  ar: 'منتهي الصلاحية', en: 'Expired' },
  accepted:         { tone: 'success', ar: 'مقبول', en: 'Accepted' },
  cleared:          { tone: 'success', ar: 'مُعتمد', en: 'Cleared' },
  bounced:          { tone: 'danger',  ar: 'مرتجع', en: 'Bounced' },
  credited:         { tone: 'purple',  ar: 'دائنة', en: 'Credited' },
  retrying:         { tone: 'warning', ar: 'إعادة محاولة', en: 'Retrying' },
  void:             { tone: 'neutral', ar: 'ملغى', en: 'Void' },
  // S12 Step-3: statuses from customer/quotation/project/payment/supplier lists.
  lead:                 { tone: 'info',    ar: 'محتمل', en: 'Lead' },
  suspended:            { tone: 'warning', ar: 'معلّق', en: 'Suspended' },
  archived:             { tone: 'neutral', ar: 'مؤرشف', en: 'Archived' },
  inactive:             { tone: 'neutral', ar: 'غير نشط', en: 'Inactive' },
  blacklisted:          { tone: 'danger',  ar: 'محظور', en: 'Blacklisted' },
  final:                { tone: 'success', ar: 'نهائي', en: 'Final' },
  recorded:             { tone: 'neutral', ar: 'مسجّلة', en: 'Recorded' },
  pending_confirmation: { tone: 'warning', ar: 'بانتظار التأكيد', en: 'Pending confirmation' },
  confirmed:            { tone: 'success', ar: 'مؤكّدة', en: 'Confirmed' },
  partially_reconciled: { tone: 'warning', ar: 'مسوّاة جزئيًا', en: 'Partially reconciled' },
  reconciled:           { tone: 'success', ar: 'مسوّاة', en: 'Reconciled' },
  // Supplier transfers (mig 437): the disbursement is sent to the bank but not yet settled.
  in_transit:           { tone: 'info',    ar: 'قيد التحويل', en: 'In Transit' },
  bank_check_pending:   { tone: 'warning', ar: 'بانتظار فحص البنك', en: 'Bank check pending' },
  disputed:             { tone: 'danger',  ar: 'متنازع عليها', en: 'Disputed' },
  refunded:             { tone: 'neutral', ar: 'مستردّة', en: 'Refunded' },
  // S14 Step-4: Tasks pack work-item statuses (mig 164). 'cancelled'/'overdue' already exist
  // above; the Task Inbox renders a cancelled task with tone='neutral' (muted) per the Make design.
  open:                 { tone: 'info',    ar: 'مفتوحة', en: 'Open' },
  in_progress:          { tone: 'warning', ar: 'جارية', en: 'In Progress' },
  done:                 { tone: 'success', ar: 'مكتملة', en: 'Done' },
};

const BY_AR: Record<string, StatusDef> = Object.values(REGISTRY).reduce(
  (m, d) => { m[d.ar] = d; return m; },
  {} as Record<string, StatusDef>,
);

export function resolveStatus(status: string): StatusDef {
  const key = (status ?? '').toString().toLowerCase().trim();
  return REGISTRY[key] ?? BY_AR[(status ?? '').toString().trim()]
    ?? { tone: 'neutral', ar: (status ?? '').toString(), en: (status ?? '').toString() };
}

export interface LxStatusProps {
  /** DB status key (e.g. "issued") or an Arabic label (e.g. "صادرة"). */
  status: string;
  locale?: 'ar' | 'en';
  /** Force a tone, overriding the registry (rare). */
  tone?: StatusTone;
  /**
   * Override the displayed text, overriding the registry label. Use ONLY for
   * domain modules whose status keys collide with generic ones or carry a
   * grammatically/semantically distinct label (e.g. a cheque "pending" =
   * "بانتظار الإيداع", not the generic "معلّقة"). The canonical chrome + tone
   * still apply, so the pill stays visually consistent platform-wide.
   */
  label?: string;
  className?: string;
}

export function LxStatus({ status, locale = 'ar', tone, label, className }: LxStatusProps) {
  const def = resolveStatus(status);
  const t = TONES[tone ?? def.tone];
  const text = label ?? (locale === 'ar' ? def.ar : def.en);
  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '4px 10px',
        borderRadius: 999,
        backgroundColor: t.bg,
        color: t.text,
        fontSize: 12,
        fontWeight: 500,
        fontFamily: 'var(--lx-font-arabic, Tajawal), sans-serif',
        whiteSpace: 'nowrap',
      }}
    >
      {text}
    </span>
  );
}

export { TONES as LX_STATUS_TONES, REGISTRY as LX_STATUS_REGISTRY };
